<script setup lang="ts">
/**
 * EU AI Act Art. 4 — the onboarding video, required before an assistant
 * opens (app/onboarding.py). Blocking like the policy gate: a video someone
 * can close is not evidence that they watched it.
 *
 * Subtitles are available from the player's CC button but off by default:
 * shown, they cover the lower part of the picture, which the video uses.
 *
 * The player lets people rewind but not jump ahead of what they have seen,
 * and keeps playback at normal speed. That is courtesy, not the control: the
 * server refuses a completion that arrives sooner than the video could have
 * been watched, whatever this component reports.
 */
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import {
  completeOnboarding,
  fetchOnboardingAsset,
  startOnboarding,
  type OnboardingVideo,
} from "../api/conversations";

const props = defineProps<{
  video: OnboardingVideo;
  devUser?: string;
}>();

const emit = defineEmits<{
  (e: "completed"): void;
  (e: "leave"): void;
}>();

const player = ref<HTMLVideoElement | null>(null);
const src = ref("");
const captions = ref("");
const loadShare = ref(0);
const loadError = ref("");
const started = ref(false);
const ended = ref(false);
const confirmed = ref(false);
const saving = ref(false);
const saveError = ref("");

// Furthest point reached by playing, and seconds actually played.
let maxReached = 0;
let lastTime = 0;
const watched = ref(0);

const enoughWatched = computed(() => watched.value >= props.video.duration_s * 0.95);
const minutes = computed(() => Math.round(props.video.duration_s / 60));

onMounted(async () => {
  try {
    src.value = await fetchOnboardingAsset(props.video.id, "media", props.devUser, (s) => {
      loadShare.value = s;
    });
    if (props.video.has_captions) {
      captions.value = await fetchOnboardingAsset(props.video.id, "captions", props.devUser)
        .catch(() => "");
    }
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : String(e);
  }
});

onBeforeUnmount(() => {
  if (src.value) URL.revokeObjectURL(src.value);
  if (captions.value) URL.revokeObjectURL(captions.value);
});

function onPlay(): void {
  if (!started.value) {
    started.value = true;
    void startOnboarding(props.video.id, props.devUser);
  }
}

/** Pull the playhead back if it is ahead of what has been watched. Called
 * from both `seeking` and `timeupdate`: Chrome can report the new position
 * in a `timeupdate` BEFORE the `seeking` event, so a guard on `seeking`
 * alone let a jump count as "reached" (found in the pilot, 2026-09-30). */
function holdBack(el: HTMLVideoElement): boolean {
  if (el.currentTime > maxReached + 1) {
    el.currentTime = maxReached;
    lastTime = maxReached;
    return true;
  }
  return false;
}

function onTimeUpdate(): void {
  const el = player.value;
  if (!el) return;
  const t = el.currentTime;
  const delta = t - lastTime;
  // Only continuous playback moves the "reached" mark and counts as watched;
  // anything else is a jump, and a jump ahead is taken back.
  if (delta > 0 && delta < 1.5) {
    watched.value += delta;
    if (t > maxReached) maxReached = t;
    lastTime = t;
    return;
  }
  if (!holdBack(el)) lastTime = t;
}

function onSeeking(): void {
  const el = player.value;
  if (!el) return;
  if (!holdBack(el)) lastTime = el.currentTime;
}

function onLoadedMetadata(): void {
  // Subtitles start OFF: shown, they cover the lower part of the picture.
  // The `default` attribute is gone, but a browser may still show a track;
  // switch it off explicitly. The CC button turns it on.
  const el = player.value;
  if (!el) return;
  for (const track of Array.from(el.textTracks)) track.mode = "disabled";
}

const watchedPct = computed(() =>
  Math.min(100, Math.round((watched.value / props.video.duration_s) * 100)),
);
const confirmHint = computed(() => {
  if (ended.value && enoughWatched.value) return "";
  if (ended.value) {
    return `Du har set ${watchedPct.value} % af videoen. Spol tilbage og se de dele, du mangler, for at kunne bekræfte.`;
  }
  return `Du kan bekræfte, når du har set hele videoen (${watchedPct.value} % set).`;
});

function onRateChange(): void {
  const el = player.value;
  if (el && el.playbackRate > 1) el.playbackRate = 1;
}

function onEnded(): void {
  ended.value = true;
}

async function finish(): Promise<void> {
  if (saving.value || !confirmed.value) return;
  saving.value = true;
  saveError.value = "";
  const why = await completeOnboarding(props.video.id, watched.value, props.devUser);
  saving.value = false;
  if (why) {
    saveError.value = `Det blev ikke registreret: ${why}`;
    return;
  }
  emit("completed");
}
</script>

<template>
  <div class="onb" role="dialog" aria-modal="true" aria-labelledby="onb-h">
    <div class="onb__card">
      <h2 id="onb-h" class="onb__title">{{ props.video.title }}</h2>
      <p class="onb__lead">
        Før du bruger assistenten, skal du se denne introduktion
        (ca. {{ minutes }} min). Du skal kun se den én gang. Du kan spole
        tilbage, men ikke frem — når hele videoen er set, kan du bekræfte og
        fortsætte.
      </p>

      <div class="onb__stage">
        <p v-if="loadError" class="onb__error">
          Videoen kunne ikke hentes ({{ loadError }}). Prøv igen om lidt.
        </p>
        <div v-else-if="!src" class="onb__loading" aria-live="polite">
          <span>Henter video… {{ Math.round(loadShare * 100) }}%</span>
          <span class="onb__bar"><span :style="{ width: `${loadShare * 100}%` }"></span></span>
        </div>
        <video
          v-else
          ref="player"
          class="onb__video"
          :src="src"
          controls
          controlslist="nodownload noplaybackrate"
          disablepictureinpicture
          playsinline
          @play="onPlay"
          @timeupdate="onTimeUpdate"
          @seeking="onSeeking"
          @ratechange="onRateChange"
          @ended="onEnded"
          @loadedmetadata="onLoadedMetadata"
        >
          <track
            v-if="captions"
            kind="captions"
            srclang="en"
            label="English"
            :src="captions"
          />
        </video>
      </div>

      <label class="onb__confirm" :class="{ 'onb__confirm--off': !ended || !enoughWatched }">
        <input
          v-model="confirmed"
          type="checkbox"
          :disabled="!ended || !enoughWatched"
        />
        Jeg har set videoen og forstået indholdet.
      </label>
      <p v-if="confirmHint" class="onb__hint" aria-live="polite">{{ confirmHint }}</p>

      <p v-if="saveError" class="onb__error">{{ saveError }}</p>

      <div class="onb__actions">
        <button class="onb__leave" type="button" @click="emit('leave')">
          Tilbage til forsiden
        </button>
        <button
          class="onb__accept"
          type="button"
          :disabled="!confirmed || saving"
          @click="finish"
        >
          {{ saving ? "Registrerer…" : "Fortsæt" }}
        </button>
      </div>

      <p class="onb__meta">
        At du har set videoen, registreres med dato og version
        ({{ props.video.version }}), som dokumentation for AI-træning.
      </p>
    </div>
  </div>
</template>

<style scoped>
.onb {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: var(--space-4);
  background: color-mix(in srgb, var(--color-bg) 82%, transparent);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.onb__card {
  width: min(56rem, 100%);
  max-height: 94vh;
  overflow-y: auto;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg, 0 24px 48px rgb(0 0 0 / 0.18));
  padding: var(--space-5, 2rem);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.onb__title {
  margin: 0;
  font-size: 1.35rem;
  line-height: 1.2;
  text-wrap: balance;
}
.onb__lead,
.onb__meta {
  margin: 0;
  color: var(--color-muted);
}
.onb__meta {
  font-size: 0.8rem;
}
.onb__stage {
  aspect-ratio: 16 / 9;
  width: 100%;
  background: #000;
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  display: grid;
  place-items: center;
}
.onb__video {
  width: 100%;
  height: 100%;
}
.onb__loading {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  color: #fff;
  font-size: 0.9rem;
}
.onb__bar {
  width: 12rem;
  height: 4px;
  background: rgb(255 255 255 / 0.25);
  border-radius: 2px;
  overflow: hidden;
}
.onb__bar > span {
  display: block;
  height: 100%;
  background: var(--color-accent);
}
.onb__confirm {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.onb__hint {
  margin: -0.4rem 0 0 1.6rem;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.onb__confirm--off {
  color: var(--color-muted);
}
.onb__actions {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
}
.onb__accept {
  padding: 0.6rem 1.4rem;
  font: inherit;
  border: none;
  border-radius: var(--radius-pill, 999px);
  background: var(--color-accent);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}
.onb__accept:hover:not(:disabled) {
  background: var(--color-accent-hover, var(--color-accent));
}
.onb__accept:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.onb__leave {
  padding: 0.6rem 1rem;
  font: inherit;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill, 999px);
  background: transparent;
  color: var(--color-ink);
  cursor: pointer;
}
.onb__error {
  margin: 0;
  color: var(--color-error);
}
@media (max-width: 480px) {
  .onb__card {
    padding: var(--space-4);
  }
}
</style>
